import React, { useState } from "react";
import axios from "axios";
import { toast } from "sonner";
import { Mail, Send, Lock } from "lucide-react";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export default function NewsletterContact() {
  const [news, setNews] = useState("");
  const [newsLoading, setNewsLoading] = useState(false);

  const [form, setForm] = useState({
    name: "",
    email: "",
    company: "",
    interest: "audit",
    message: "",
  });
  const [submitting, setSubmitting] = useState(false);

  const submitNewsletter = async (e) => {
    e.preventDefault();
    if (!news) return;
    setNewsLoading(true);
    try {
      await axios.post(`${API}/newsletter`, { email: news });
      toast.success("Subscribed. The next briefing is on its way.");
      setNews("");
    } catch (err) {
      toast.error("Could not subscribe. Check the address and try again.");
    } finally {
      setNewsLoading(false);
    }
  };

  const submitContact = async (e) => {
    e.preventDefault();
    setSubmitting(true);
    try {
      await axios.post(`${API}/contact`, form);
      toast.success("Inquiry filed. Our team will reply within 24 hours.");
      setForm({ name: "", email: "", company: "", interest: "audit", message: "" });
    } catch (err) {
      toast.error("Submission failed. Please retry.");
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <section
      id="contact"
      data-testid="contact-section"
      className="relative border-b border-white/10 py-24 md:py-32 bg-[#070707]"
    >
      <div className="absolute inset-0 -z-10 bg-grid opacity-30" />

      <div className="max-w-7xl mx-auto px-6 md:px-12 grid grid-cols-1 lg:grid-cols-12 gap-12 lg:gap-16">
        {/* Newsletter */}
        <div className="lg:col-span-5 reveal">
          <div className="font-mono text-[11px] uppercase tracking-[0.32em] text-red-400 mb-6">
            /03 — Briefing
          </div>
          <h2 className="font-heading text-4xl md:text-5xl font-bold uppercase tracking-tight leading-[0.95]">
            The weekly
            <br />
            <span className="text-red-500">field briefing.</span>
          </h2>
          <p className="mt-8 font-body text-base text-gray-300 leading-relaxed">
            One signal-dense email each week. New exposed files, fresh algorithm changes that
            moved revenue, and one defensive play you can run that day.
          </p>

          <form onSubmit={submitNewsletter} className="mt-10" data-testid="newsletter-form">
            <label className="font-mono text-[11px] uppercase tracking-[0.24em] text-gray-400">
              Work Email
            </label>
            <div className="mt-3 flex items-center border-b border-white/20 focus-within:border-red-500 transition-colors">
              <Mail size={16} className="text-gray-500" />
              <input
                type="email"
                required
                value={news}
                onChange={(e) => setNews(e.target.value)}
                placeholder="you@company.com"
                data-testid="newsletter-email-input"
                className="flex-1 bg-transparent border-0 outline-none px-3 py-3 text-white placeholder-gray-600 font-body"
              />
              <button
                type="submit"
                disabled={newsLoading}
                data-testid="newsletter-submit"
                className="font-mono text-[11px] uppercase tracking-[0.24em] text-red-400 hover:text-red-300 disabled:opacity-50 transition-colors"
              >
                {newsLoading ? "Sending…" : "Subscribe →"}
              </button>
            </div>
            <p className="mt-5 font-mono text-[10px] uppercase tracking-[0.22em] text-gray-500 flex items-center gap-2">
              <Lock size={11} /> No spam. Unsubscribe with one click.
            </p>
          </form>
        </div>

        {/* Contact form */}
        <div className="lg:col-span-7">
          <div className="border border-white/10 bg-[#0a0a0a] p-8 md:p-12 reveal">
            <div className="flex items-center justify-between mb-10">
              <div>
                <div className="font-mono text-[11px] uppercase tracking-[0.32em] text-red-400">
                  /04 — Consultation
                </div>
                <h3 className="mt-4 font-heading text-2xl md:text-3xl font-bold uppercase tracking-tight">
                  Engage the team.
                </h3>
              </div>
              <div className="hidden md:flex items-center gap-2 font-mono text-[10px] uppercase tracking-[0.24em] text-gray-500">
                <span className="h-1.5 w-1.5 bg-green-500 pulse-dot" /> Reply &lt; 24h
              </div>
            </div>

            <form onSubmit={submitContact} className="grid grid-cols-1 md:grid-cols-2 gap-8" data-testid="contact-form">
              {[
                { name: "name", label: "Name", type: "text", required: true, span: 1 },
                { name: "email", label: "Email", type: "email", required: true, span: 1 },
                { name: "company", label: "Company", type: "text", required: false, span: 2 },
              ].map((f) => (
                <div key={f.name} className={f.span === 2 ? "md:col-span-2" : ""}>
                  <label className="font-mono text-[10px] uppercase tracking-[0.26em] text-gray-400">
                    {f.label}
                  </label>
                  <input
                    type={f.type}
                    required={f.required}
                    value={form[f.name]}
                    onChange={(e) => setForm({ ...form, [f.name]: e.target.value })}
                    data-testid={`contact-${f.name}-input`}
                    className="w-full mt-3 bg-transparent border-b border-white/20 focus:border-red-500 outline-none px-0 py-3 text-white placeholder-gray-600 font-body transition-colors"
                  />
                </div>
              ))}

              <div className="md:col-span-2">
                <label className="font-mono text-[10px] uppercase tracking-[0.26em] text-gray-400">
                  Interest
                </label>
                <div className="mt-4 flex flex-wrap gap-3">
                  {[
                    { v: "audit", l: "Risk Audit" },
                    { v: "intel", l: "Threat Intel" },
                    { v: "counter", l: "Counter-Strategy" },
                    { v: "defense", l: "Defense Program" },
                    { v: "press", l: "Press / Policy" },
                  ].map((opt) => (
                    <button
                      type="button"
                      key={opt.v}
                      onClick={() => setForm({ ...form, interest: opt.v })}
                      data-testid={`interest-${opt.v}`}
                      className={`px-4 py-2 font-mono text-[10px] uppercase tracking-[0.22em] border transition-colors ${
                        form.interest === opt.v
                          ? "bg-red-600 border-red-600 text-white"
                          : "border-white/20 text-gray-300 hover:border-white/60"
                      }`}
                    >
                      {opt.l}
                    </button>
                  ))}
                </div>
              </div>

              <div className="md:col-span-2">
                <label className="font-mono text-[10px] uppercase tracking-[0.26em] text-gray-400">
                  Brief
                </label>
                <textarea
                  required
                  rows={4}
                  value={form.message}
                  onChange={(e) => setForm({ ...form, message: e.target.value })}
                  data-testid="contact-message-input"
                  placeholder="What system, score, or platform is moving against you?"
                  className="w-full mt-3 bg-transparent border-b border-white/20 focus:border-red-500 outline-none px-0 py-3 text-white placeholder-gray-600 font-body resize-none transition-colors"
                />
              </div>

              <div className="md:col-span-2 flex flex-col sm:flex-row sm:items-center sm:justify-between gap-6 pt-4 border-t border-white/10">
                <p className="font-mono text-[10px] uppercase tracking-[0.22em] text-gray-500 max-w-sm">
                  Submissions are confidential. Engagements begin with a 30-minute discovery call.
                </p>
                <button
                  type="submit"
                  disabled={submitting}
                  data-testid="contact-submit"
                  className="inline-flex items-center justify-center gap-2 bg-red-600 hover:bg-red-500 px-8 py-4 font-mono text-xs uppercase tracking-[0.24em] text-white transition-colors disabled:opacity-50"
                >
                  {submitting ? "Transmitting…" : "Send Brief"}
                  <Send size={14} />
                </button>
              </div>
            </form>
          </div>
        </div>
      </div>
    </section>
  );
}
